import { useState, useEffect, useCallback } from 'react';
import {
  Check, Clock, XCircle, Map, Lock, Unlock,
  Award, Briefcase, GraduationCap, Stethoscope, Scale, Building2,
  Mic, Video, Music, Palette, BookOpen, School,
  Hotel, Plane, FlaskConical, Pill, HeartPulse,
  Code, Landmark, FileText, Train, Banknote, Target,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import PlanUpgradeModal from '@components/common/PlanUpgradeModal';

// Global flag to prevent multiple components from fetching simultaneously
let globalCategoriesFetchInProgress = false;
let globalCategoriesHasFetched = false;
let globalProgressFetchInProgress = false;

const SUBCAT_ICONS = {
  ca: Award,
  cs: Briefcase,  
  cfa: GraduationCap,
  doctor: Stethoscope,
  lawyer: Scale,
  mba: Building2,
  'standup-comedian': Mic,
  'content-creator': Video,
  'professional-dancer': Music,
  'professional-singer': Mic,
  'professional-artist': Palette,
  prt: BookOpen,
  tgt: BookOpen,
  pgt: School,
  'asst-prof': GraduationCap,
  'hotel-mgmt': Hotel,
  'cabin-crew': Plane,
  bsc: FlaskConical,
  pharmacy: Pill,
  nursing: HeartPulse,
  aviation: Plane,
  swe: Code,
  upsc: Landmark,
  ssc: FileText,
  railway: Train,
  bank: Banknote,
  'state-psc': Building2,
  'fresher-path': Target,
};
import careerMapData from '@data/careerMapData.json';
import careerMapService from '@/services/careerMapService';
import profileService from '@/services/profile.service';
import Button from '@/components/common/Button';

const CareerMap = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [stageData, setStageData] = useState({});
  const [lockedSubCategory, setLockedSubCategory] = useState(null);
  const [subcategoryMap, setSubcategoryMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState(careerMapData.categories);
  const [backendCategories, setBackendCategories] = useState([]);
  const [expandedStages, setExpandedStages] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const {
    canAccessCareerCategory,
    canAccessCareerSubcategory,
    filterCareerCategories,
    filterCareerSubcategories,
    getCareerCategoryRestrictionInfo,
    getCareerSubcategoryRestrictionInfo
  } = usePlanRestrictions();
  const currentCategory = categories.find(cat => cat.slug === selectedCategory || cat.id === selectedCategory);
  const currentSubCategory = currentCategory?.subcategories?.find(sub => sub.slug === selectedSubCategory || sub.id === selectedSubCategory);
  const stages = currentSubCategory?.stages || [];
  const selectedStage = selectedStageId ? stages.find(s => s.id === selectedStageId) : null;

  useEffect(() => {
    // If data hasn't been fetched yet, fetch it
    if (!globalCategoriesHasFetched) {
      loadBackendData();
    } else {
      // Data already exists, just load it from the existing state
      // This handles the case when returning to the page
      if (categories.length > 0 && Object.keys(subcategoryMap).length === 0) {
        const map = {};
        categories.forEach(cat => {
          cat.subcategories?.forEach(sub => {
            map[sub.id] = sub;
            map[sub.slug] = sub;
          });
        });
        setSubcategoryMap(map);
      }
    }

    // Cleanup: Reset flags when component unmounts
    return () => {
      globalCategoriesHasFetched = false;
      globalProgressFetchInProgress = false;
    };
  }, []);

  const loadBackendData = async () => {
    // Prevent multiple simultaneous fetches
    if (globalCategoriesFetchInProgress || globalCategoriesHasFetched) {
      return;
    }

    try {
      globalCategoriesFetchInProgress = true;
      setLoading(true);
      const categoriesData = await careerMapService.getCategories();
      setBackendCategories(categoriesData);
      const mergedCategories = careerMapData.categories.map(localCat => {
        const backendCat = categoriesData.find(bc => bc.slug === localCat.id);
        if (!backendCat) return localCat;
        return {
          ...localCat,
          id: backendCat.id,
          slug: localCat.id, // Preserve original slug for restriction checking
          subcategories: localCat.subCategories.map(localSub => {
            const backendSub = backendCat.subcategories?.find(bs => bs.slug === localSub.id);
            return {
              ...localSub,
              id: backendSub?.id || localSub.id,
              slug: localSub.id,
              stages: localSub.stages
            };
          })
        };
      });
      setCategories(mergedCategories);
      const map = {};
      mergedCategories.forEach(cat => {
        cat.subcategories?.forEach(sub => {
          map[sub.id] = sub;
          map[sub.slug] = sub;
        });
      });
      setSubcategoryMap(map);
      globalCategoriesHasFetched = true;
      await loadUserProgress();
    } catch (error) {
      console.error('❌ Error loading backend data:', error);
    } finally {
      setLoading(false);
      globalCategoriesFetchInProgress = false;
    }
  };

  const loadUserProgress = async () => {
    // Prevent multiple simultaneous fetches
    if (globalProgressFetchInProgress) {
      return;
    }

    try {
      const token = careerMapService.getAuthToken();
      if (!token) return;
      globalProgressFetchInProgress = true;
      setLoading(true);
      const progress = await careerMapService.getUserProgress();
      const newStageData = {};
      progress.forEach(p => {
        const key = `${p.subcategory.slug}_${p.stage.stageNumber}`;
        newStageData[key] = { status: p.status, ...p.fieldData };
      });
      setStageData(newStageData);
    } catch (error) {
      console.error('❌ Error loading progress:', error);
    } finally {
      setLoading(false);
      globalProgressFetchInProgress = false;
    }
  };

  const getKey = (subId, stageId) => `${subId}_${stageId}`;
  const getStageStatus = (stageId) => stageData[getKey(selectedSubCategory, stageId)]?.status || 'not-started';
  const getStageFormData = (stageId) => stageData[getKey(selectedSubCategory, stageId)] || {};

  const updateStageData = (stageId, field, value) => {
    const key = getKey(selectedSubCategory, stageId);
    setStageData(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const calculateProgress = () => {
    if (!stages.length) return 0;
    const done = stages.filter(s => getStageStatus(s.id) === 'completed').length;
    return Math.round((done / stages.length) * 100);
  };

  const completedCount = stages.filter(s => getStageStatus(s.id) === 'completed').length;
  const progressPercentage = calculateProgress();

  const circleClass = (status) => {
    if (status === 'completed') return 'circle-done';
    if (status === 'in-progress') return 'circle-inprog';
    return 'circle-default';
  };

  const badgeClass = (status) => {
    if (status === 'completed') return 'badge-done';
    if (status === 'in-progress') return 'badge-inprog';
    return 'badge-none';
  };

  const badgeLabel = (status) => {
    if (status === 'completed') return 'Achieved';
    if (status === 'in-progress') return 'In Progress';
    return 'Not Started';
  };

  const saveStageData = async () => {
    if (!selectedStageId) return;
    try {
      const token = careerMapService.getAuthToken();
      if (!token) { setSelectedStageId(null); return; }
      const subcategoryId = careerMapService.findSubcategoryId(categories, selectedSubCategory);
      if (!subcategoryId) { setSelectedStageId(null); return; }
      const subcategoryData = await careerMapService.getSubcategory(subcategoryId);
      const backendStageId = subcategoryData.stages.find(s => s.stageNumber === selectedStageId)?.id;
      if (!backendStageId) { setSelectedStageId(null); return; }
      const key = getKey(selectedSubCategory, selectedStageId);
      const currentData = stageData[key] || {};
      const { status, ...fieldData } = currentData;
      const finalStatus = status || 'in-progress';
      const result = await careerMapService.saveProgress(subcategoryId, backendStageId, finalStatus, fieldData);
      setSelectedStageId(null);
    } catch (error) {
      console.error('❌ Error saving progress:', error);
      setSelectedStageId(null);
    }
  };

  if (loading && !categories.length) {
    return (
      <div className="w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <div className="text-base md:text-lg text-gray-500">Loading career map data...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        /* ─── Reset ─────────────────────────────────────────────── */
        .cm-page *, .cm-page *::before, .cm-page *::after { box-sizing: border-box; }

        .cm-page {
          width: 100%;
          max-width: 100%;
          margin: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
        }

        /* ─── CSS custom props (single source of truth) ───────── */
        .cm-page {
          --accent:        #c33c3d;
          --accent-dark:   #a32f30;
          --accent-darker: #8b2829;
          --accent-light:  #fce8e9;
          --accent-mid:    #fdd5d7;
          --green:         #10b981;
          --green-dark:    #059669;
          --radius-sm:     0.5rem;
          --radius-md:     0.75rem;
          --radius-lg:     1rem;
          --shadow-xs:     0 1px 2px rgba(0,0,0,.05);
          --shadow-sm:     0 1px 3px rgba(0,0,0,.05), 0 4px 12px rgba(0,0,0,.03);
          --shadow-md:     0 4px 12px rgba(0,0,0,.08), 0 8px 24px rgba(0,0,0,.05);
          --shadow-accent: 0 4px 12px rgba(195,60,61,.3);
          --shadow-accent-lg: 0 6px 16px rgba(195,60,61,.4);
        }

        /* ─── Page Header (matches Tailwind: bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4) ─── */
        .cm-page-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          margin-bottom: 1rem;
          box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
          max-width: 100%;
          overflow: hidden;
        }
        @media (min-width: 640px) {
          .cm-page-header { gap: 0.75rem; padding: 1rem; }
        }

        /* h1/p direct child becomes the truncating text container */
        .cm-page-header > div:not(.cm-page-icon) {
          min-width: 0;
          flex: 1 1 0%;
        }

        .cm-page-icon {
          width: 2rem; height: 2rem;
          display: flex; align-items: center; justify-content: center;
          background: #fee2e2;
          border-radius: 0.5rem;
          flex-shrink: 0;
        }
        .cm-page-icon svg { width: 1rem; height: 1rem; color: #c33c3d; }
        @media (min-width: 640px) {
          .cm-page-icon { width: 2.5rem; height: 2.5rem; }
          .cm-page-icon svg { width: 1.25rem; height: 1.25rem; }
        }

        .cm-page-title {
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          margin: 0;
          line-height: 1.25;
          letter-spacing: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (min-width: 640px) {
          .cm-page-title { font-size: 1.125rem; }
        }
        .cm-page-subtitle {
          font-size: 0.75rem;
          color: #4b5563;
          margin: 0;
          line-height: 1.5;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (min-width: 640px) {
          .cm-page-subtitle { font-size: 0.875rem; }
        }

        /* ─── Category Tabs ──────────────────────────────────────── */
        .cm-tabs {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-xs);
        }
        .cm-tab {
          flex: 1 1 auto;
          min-width: fit-content;
          padding: 0.5rem 1.25rem;
          border-radius: var(--radius-sm);
          font-size: clamp(0.75rem, 1.8vw, 0.875rem);
          font-weight: 600;
          cursor: pointer;
          border: 1px solid transparent;
          background: #f9fafb;
          color: #4b5563;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          letter-spacing: -0.01em;
          white-space: nowrap;
          text-align: center;
        }
        .cm-tab:hover { background: #f3f4f6; color: #1f2937; transform: translateY(-1px); }
        .cm-tab.active {
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
          border-color: var(--accent); color: #fff;
          box-shadow: var(--shadow-accent);
        }

        /* ─── Subcategory Chips ─────────────────────────────────── */
        .cm-subcats {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-md);
          box-shadow: var(--shadow-xs);
        }
        .cm-subcat {
          display: inline-flex; align-items: center; justify-content: center; gap: 0.375rem;
          padding: 0.375rem 0.875rem;
          border-radius: 0.375rem;
          border: 1px solid #e5e7eb;
          background: #fff;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          flex: 0 1 auto;
          min-width: 0;
          font-size: 0.75rem;
          line-height: 1.25;
        }
        @media (min-width: 640px) {
          .cm-subcat { font-size: 13px; }
        }
        .cm-subcat:hover { border-color: var(--accent); background: #fafafa; }
        .cm-subcat.active { border-color: var(--accent); background: linear-gradient(135deg, #fff 0%, #fef2f2 100%); }
        .cm-subcat.locked { border-color: var(--green); background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%); }
        .cm-subcat-name { font-size: inherit; line-height: inherit; font-weight: 600; color: #1f2937; letter-spacing: -0.01em; white-space: nowrap; }
        .cm-subcat-pct { font-size: 0.6875rem; padding: 0 0.375rem; border-radius: 0.25rem; background: #f3f4f6; color: #4b5563; font-weight: 700; white-space: nowrap; line-height: 1.25; }
        .cm-subcat.active .cm-subcat-pct { background: var(--accent-light); color: var(--accent); }

        /* ─── Cards ─────────────────────────────────────────────── */
        .cm-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-lg);
          padding: clamp(0.875rem, 2.5vw, 1.5rem) clamp(0.875rem, 3vw, 1.75rem);
          margin-bottom: 1rem;
          box-shadow: var(--shadow-sm);
          transition: box-shadow 0.2s ease;
        }
        .cm-card:hover { box-shadow: var(--shadow-md); }
        .cm-card.static-card { background: #fff; }

        .cm-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #f3f4f6;
          flex-wrap: wrap;
        }
        .cm-card-title { font-size: 1rem; font-weight: 700; color: #151e32; margin-bottom: 0.375rem; letter-spacing: -0.01em; }
        .cm-card-duration { font-size: clamp(0.6875rem, 1.6vw, 0.8125rem); color: #6b7280; font-weight: 500; }
        .cm-card-right { text-align: right; flex-shrink: 0; }
        .cm-pct-val { font-size: clamp(1.125rem, 3vw, 1.5rem); font-weight: 800; color: var(--accent); letter-spacing: -0.03em; }
        .cm-stages-count { font-size: clamp(0.625rem, 1.5vw, 0.75rem); color: #9ca3af; margin-top: 0.25rem; font-weight: 500; }
        .cm-reset-btn { font-size: clamp(0.625rem, 1.5vw, 0.75rem); color: #6b7280; background: none; border: none; cursor: pointer; padding: 0.25rem 0; margin-top: 0.375rem; display: block; font-weight: 600; transition: color 0.2s; }
        .cm-reset-btn:hover { color: var(--accent); text-decoration: underline; }

        /* ─── Progress Bar ──────────────────────────────────────── */
        .cm-progress-bar { height: 0.5rem; background: #e5e7eb; border-radius: 999px; margin-bottom: 1.25rem; overflow: hidden; box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05); }
        .cm-progress-fill { height: 100%; background: linear-gradient(90deg, var(--accent) 0%, #e74c3c 100%); border-radius: 999px; transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1); box-shadow: 0 2px 8px rgba(195, 60, 61, 0.3); }

        /* ─── Timeline (horizontal scroll on mobile) ─────────────── */
        .cm-timeline {
          display: flex;
          align-items: flex-start;
          overflow-x: auto;
          padding-bottom: 0.75rem;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: #d1d5db #f3f4f6;
          padding-left: 0.25rem;
          padding-right: 0.25rem;
        }
        .cm-timeline::-webkit-scrollbar { height: 0.375rem; }
        .cm-timeline::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 999px; }
        .cm-timeline::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 999px; }
        .cm-timeline::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

        /* ─── Stage Wrapper & Stage ──────────────────────────────── */
        .cm-stage-wrap { display: flex; align-items: flex-start; flex-shrink: 0; }

        /* 
          Responsive stage width:
          xs  (<480px)  → 5.5rem
          sm  (480-640) → 7rem
          md+ (>640px)  → 8.75rem
          Done via clamp on a viewport unit that stops changing past 1024px
        */
        .cm-stage {
          display: flex; flex-direction: column; align-items: center;
          width: clamp(4.5rem, 11vw, 7rem);
          padding: 0 0.25rem;
          position: relative;
        }
        .cm-stage.clickable { cursor: pointer; }
        .cm-stage.clickable:hover .cm-circle { transform: scale(1.08); box-shadow: 0 6px 20px rgba(195, 60, 61, 0.35); }

        /* ─── Circles ──────────────────────────────────────────── */
        .cm-circle {
          width: clamp(2rem, 4.5vw, 2.5rem);
          height: clamp(2rem, 4.5vw, 2.5rem);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.8125rem;
          font-weight: 700; flex-shrink: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        }
        .circle-default { background: #fff; border: 2px solid #d1d5db; color: #9ca3af; }
        .circle-done    { background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%); border: 2px solid var(--accent); color: #fff; box-shadow: 0 4px 16px rgba(195, 60, 61, 0.4); }
        .circle-inprog  { background: linear-gradient(135deg, var(--accent-light) 0%, var(--accent-mid) 100%); border: 2px solid var(--accent); color: var(--accent); box-shadow: 0 4px 12px rgba(195, 60, 61, 0.2); }

        /* ─── Stage Info ──────────────────────────────────────── */
        .cm-stage-info {
          text-align: center;
          margin-top: 0.5rem;
          width: 100%;
          display: flex; flex-direction: column; align-items: center;
          padding: 0.5rem 0.375rem;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          background: #fff;
        }
        .cm-stage-name {
          font-size: 0.6875rem;
          font-weight: 700; color: #111827; line-height: 1.3;
          margin-bottom: 0.25rem; letter-spacing: -0.01em;
        }
        .cm-stage-dur {
          font-size: 0.625rem; color: #6b7280;
          background: #f3f4f6; border-radius: 0.375rem;
          padding: 0.125rem 0.375rem; display: inline-block;
          margin-bottom: 0.25rem; font-weight: 600;
          border: 1px solid #e5e7eb; white-space: nowrap;
        }
        .cm-stage-desc {
          font-size: 0.625rem; color: #6b7280; line-height: 1.4;
          margin-bottom: 0.25rem;
          word-break: break-word;
        }
        .cm-click-hint { font-size: 0.625rem; color: var(--accent); font-weight: 600; margin-top: 0.125rem; }

        /* ─── Saved Inputs Section - Simplified Single Strip ──────────────────────── */
        .cm-saved-inputs-section {
          margin-top: 0.875rem;
          padding: 0.875rem;
          background: linear-gradient(135deg, #f9fafb 0%, #ffffff 100%);
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-sm);
        }
        .cm-saved-inputs-title {
          font-size: clamp(0.8125rem, 2vw, 0.9375rem);
          font-weight: 700;
          color: #111827;
          margin: 0 0 0.625rem 0;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e5e7eb;
          letter-spacing: -0.01em;
        }
        .cm-saved-single-strip {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .cm-saved-strip-item {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: var(--radius-sm);
          padding: 0.4rem 1rem;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
          display: flex;
          gap: 0.75rem;
          align-items: stretch;
        }
        .cm-saved-strip-item:hover {
          box-shadow: 0 2px 8px rgba(195, 60, 61, 0.1);
          border-color: #fecaca;
        }
        .cm-saved-strip-bar {
          width: 0.75rem;
          flex-shrink: 0;
          display: block;
          height: 100%;
        }
        .cm-saved-strip-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
        }
        .cm-saved-strip-content {
          flex: 1;
          min-width: 0;
        }
        .cm-saved-strip-name {
          font-size: 0.875rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.25rem;
          letter-spacing: -0.01em;
        }
        .cm-saved-strip-fields {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: #4b5563;
          line-height: 1.4;
        }
        .cm-saved-strip-field {
          display: inline-block;
        }
        .cm-saved-strip-field strong {
          color: #111827;
          font-weight: 600;
        }
        .cm-saved-strip-status {
          font-size: 0.75rem;
          font-weight: 700;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .cm-saved-strip-progress {
          height: 0.375rem;
          background: #e5e7eb;
          border-radius: 999px;
          overflow: hidden;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
        }
        .cm-saved-strip-progress-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @media (max-width: 640px) {
          .cm-saved-strip-header {
            flex-wrap: wrap;
          }
          .cm-saved-strip-status {
            width: 100%;
            text-align: left;
            margin-top: 0.25rem;
          }
          .cm-saved-strip-fields {
            flex-direction: column;
            gap: 0.25rem;
          }
        }

        /* ─── Badges ─────────────────────────────────────────── */
        .cm-badge {
          font-size: clamp(0.5rem, 1.3vw, 0.6875rem);
          border-radius: 0.375rem; padding: 0.2rem 0.5rem;
          display: inline-block; font-weight: 700;
          letter-spacing: 0.02em; text-transform: uppercase;
          border: 1px solid; white-space: nowrap;
        }
        .badge-done   { background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%); color: #065f46; border-color: #6ee7b7; }
        .badge-inprog { background: linear-gradient(135deg, var(--accent-light) 0%, var(--accent-mid) 100%); color: #991b1b; border-color: #fca5a5; }
        .badge-none   { background: #f3f4f6; color: #6b7280; border-color: #d1d5db; }

        /* ─── Connector ─────────────────────────────────────── */
        .cm-connector {
          width: clamp(0.5rem, 1.5vw, 1.25rem);
          height: 2px;
          background: linear-gradient(90deg, #e5e7eb 0%, #d1d5db 50%, #e5e7eb 100%);
          margin-top: clamp(1rem, 2.5vw, 1.5rem);
          flex-shrink: 0; border-radius: 999px;
        }

        /* ─── Card label (progress section) ─────────────────── */
        .cm-card-label { font-size: 1rem; font-weight: 700; color: #151e32; letter-spacing: -0.01em; }
        .cm-card-label-sub { font-size: clamp(0.6875rem, 1.6vw, 0.8125rem); color: #6b7280; margin-top: 0.25rem; font-weight: 500; }

        /* ─── Stage detail form ─────────────────────────────── */
        .cm-form {
          background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
          border: 1px solid #e5e7eb; border-radius: var(--radius-lg);
          padding: clamp(1rem, 3vw, 1.75rem) clamp(1rem, 3.5vw, 2rem);
          margin-top: 1.25rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        .cm-form-header {
          display: flex; align-items: flex-start; gap: 1rem;
          margin-bottom: 1.25rem;
          padding-bottom: 1.125rem;
          border-bottom: 1px solid #e5e7eb;
          flex-wrap: wrap;
        }
        .cm-form-circle {
          width: clamp(2.5rem, 6vw, 3rem); height: clamp(2.5rem, 6vw, 3rem);
          border-radius: 50%;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
          display: flex; align-items: center; justify-content: center;
          font-size: clamp(0.875rem, 2vw, 1.125rem);
          font-weight: 700; color: #fff; flex-shrink: 0;
          box-shadow: var(--shadow-accent);
        }
        .cm-form-title { font-size: 1rem; font-weight: 700; color: #151e32; letter-spacing: -0.01em; }
        .cm-form-desc  { font-size: clamp(0.6875rem, 1.6vw, 0.8125rem); color: #6b7280; margin-top: 0.25rem; line-height: 1.5; }
        .cm-form-stage-meta { font-size: clamp(0.5625rem, 1.4vw, 0.75rem); color: #9ca3af; margin-bottom: 0.25rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }

        .cm-field { margin-bottom: 1rem; }
        .cm-label { font-size: clamp(0.6875rem, 1.6vw, 0.8125rem); color: #374151; margin-bottom: 0.5rem; display: block; font-weight: 600; letter-spacing: -0.01em; }
        .cm-input {
          width: 100%;
          padding: clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.625rem, 2vw, 1rem);
          border: 1px solid #e5e7eb; border-radius: var(--radius-sm);
          background: #fff; color: #111827;
          font-size: clamp(0.75rem, 1.8vw, 0.875rem);
          font-family: inherit; outline: none;
          transition: all 0.2s; box-shadow: var(--shadow-xs);
        }
        .cm-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(195, 60, 61, 0.1), var(--shadow-xs); }
        .cm-input::placeholder { color: #9ca3af; }
        .cm-textarea { resize: none; height: clamp(4rem, 10vw, 5.625rem); }
        .cm-select {
          width: 100%;
          padding: clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.625rem, 2vw, 1rem);
          border: 1px solid #e5e7eb; border-radius: var(--radius-sm);
          background: #fff; color: #111827;
          font-size: clamp(0.75rem, 1.8vw, 0.875rem);
          font-family: inherit; cursor: pointer; outline: none;
          box-shadow: var(--shadow-xs); transition: all 0.2s;
        }
        .cm-select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(195, 60, 61, 0.1), var(--shadow-xs); }

        .cm-form-actions {
          display: flex;
          justify-content: space-between; align-items: center;
          padding-top: 1.25rem;
          border-top: 1px solid #e5e7eb;
          margin-top: 0.5rem;
          gap: 0.75rem;
          flex-wrap: wrap;
        }
        .cm-btn-back {
          flex: 1 1 auto; min-width: fit-content;
          font-size: clamp(0.75rem, 1.8vw, 0.875rem); color: #4b5563;
          background: #fff; border: 1px solid #d1d5db;
          border-radius: var(--radius-sm);
          padding: clamp(0.5rem, 1.5vw, 0.75rem) clamp(0.875rem, 3vw, 1.5rem);
          cursor: pointer; font-family: inherit;
          transition: all 0.2s; font-weight: 600; box-shadow: var(--shadow-xs);
          text-align: center;
        }
        .cm-btn-back:hover { background: #f9fafb; border-color: #9ca3af; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); }
        .cm-btn-save {
          flex: 1 1 auto; min-width: fit-content;
          font-size: clamp(0.75rem, 1.8vw, 0.875rem); color: #fff;
          background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
          border: none; border-radius: var(--radius-sm);
          padding: clamp(0.5rem, 1.5vw, 0.75rem) clamp(1.125rem, 4vw, 2rem);
          cursor: pointer; font-weight: 700; font-family: inherit;
          transition: all 0.2s; box-shadow: var(--shadow-accent);
          text-align: center;
        }
        .cm-btn-save:hover { background: linear-gradient(135deg, var(--accent-dark) 0%, var(--accent-darker) 100%); transform: translateY(-2px); box-shadow: var(--shadow-accent-lg); }

        .cm-empty {
          background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
          border: 1px dashed #d1d5db; border-radius: var(--radius-lg);
          padding: clamp(2rem, 8vw, 3.75rem) clamp(1.25rem, 5vw, 2.5rem);
          text-align: center; color: #6b7280;
          font-size: clamp(0.8125rem, 2vw, 0.9375rem); font-weight: 500;
        }

        /* ══════════════════════════════════════════════════════════
           BREAKPOINT OVERRIDES
           ══════════════════════════════════════════════════════════ */

        /* ── Mobile portrait  <480px ─────────────────── */
        @media (max-width: 479px) {
          .cm-tabs           { padding: 0.5rem; gap: 0.375rem; }
          .cm-tab            { padding: 0.4rem 0.875rem; }
          .cm-subcats        { padding: 0.5rem; gap: 0.375rem; }
          .cm-card           { padding: 0.75rem; }
          .cm-card-header    { flex-direction: column; }
          .cm-card-right     { text-align: left; }
          .cm-stage          { width: 4.25rem; }
          .cm-connector      { width: 0.5rem; }
          .cm-form-header    { flex-direction: column; }
          .cm-form-actions   { flex-direction: column; }
          .cm-btn-back, .cm-btn-save { width: 100%; }
        }

        /* ── Mobile landscape / small tablet  480–767px ─────── */
        @media (min-width: 480px) and (max-width: 767px) {
          .cm-stage       { width: 5rem; }
          .cm-connector   { width: 0.75rem; }
        }

        /* ── Tablet  768–1023px ──────────────────────────────── */
        @media (min-width: 768px) and (max-width: 1023px) {
          .cm-stage     { width: 6rem; }
          .cm-connector { width: 1rem; }
        }

        /* ── Small desktop (landscape tablet / 1024×600) ─────── */
        @media (min-width: 1024px) and (max-height: 680px) {
          .cm-tabs        { padding: 0.5rem 0.75rem; margin-bottom: 0.625rem; }
          .cm-subcats     { padding: 0.5rem 0.75rem; margin-bottom: 0.625rem; }
          .cm-card        { padding: 0.875rem 1.25rem; margin-bottom: 0.625rem; }
          .cm-progress-bar{ margin-bottom: 0.875rem; }
        }

        /* ── Large desktop  ≥1280px ──────────────────────────── */
        @media (min-width: 1280px) {
          .cm-stage     { width: 7rem; }
          .cm-connector { width: 1.25rem; }
        }

        /* ── XL / 4K  ≥1920px ──────────────────────────────── */
        @media (min-width: 1920px) {
          .cm-stage     { width: 7.5rem; }
          .cm-connector { width: 1.5rem; }
        }
      `}</style>

      <div className="w-full min-w-0 max-w-full overflow-x-hidden">
        <div className="cm-page max-w-full space-y-3 sm:space-y-4">

          {/* Page Header */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4 max-w-full overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-[#fee2e2] rounded-lg flex-shrink-0">
                <Map className="w-4 h-4 sm:w-5 sm:h-5 text-[#c33c3d]" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight truncate">
                  {currentCategory ? `${currentCategory.name} Career Journeys` : 'Explore Career Journeys'}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  Explore career paths across categories. Click each stage to mark it complete.
                </p>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="cm-tabs" data-tour="career-categories">
            {categories.map(cat => {
              const categorySlug = cat.slug || cat.id;
              const hasAccess = canAccessCareerCategory(categorySlug);
              const restrictionInfo = getCareerCategoryRestrictionInfo(categorySlug);
              
              return (
                <Button
                  key={cat.id}
                  variant="pill"
                  size="md"
                  active={selectedCategory === cat.id}
                  onClick={() => {
                    if (!hasAccess) {
                      setShowUpgradeModal(true);
                      return;
                    }
                    setSelectedCategory(cat.id);
                    setSelectedSubCategory(null);
                    setLockedSubCategory(null);
                    setSelectedStageId(null);
                  }}
                  className={!hasAccess ? 'opacity-60' : ''}
                >
                  {!hasAccess && <Lock className="w-3.5 h-3.5 inline-block mr-1.5" />}
                  {cat.name}
                </Button>
              );
            })}
          </div>

          {/* Subcategory chips */}
          {(currentCategory?.subcategories?.length > 0 || currentCategory?.subCategories?.length > 0) && (
            <div className="cm-subcats" data-tour="career-subcategories">
              {(currentCategory.subcategories || currentCategory.subCategories || [])
                .filter(sub => !lockedSubCategory || sub.id === lockedSubCategory || sub.slug === lockedSubCategory)
                .map(sub => {
                  const categorySlug = currentCategory.slug || currentCategory.id;
                  const subcategorySlug = sub.slug || sub.id;
                  const hasAccess = canAccessCareerSubcategory(categorySlug, subcategorySlug);
                  const restrictionInfo = getCareerSubcategoryRestrictionInfo(categorySlug, subcategorySlug);
                  const subStages = sub.stages || [];
                  const subKey = sub.slug || sub.id;
                  const subDone = subStages.filter(s =>
                    stageData[getKey(subKey, s.id)]?.status === 'completed'
                  ).length;
                  const subPct = subStages.length ? Math.round((subDone / subStages.length) * 100) : 0;
                  const isLocked = lockedSubCategory === sub.id || lockedSubCategory === sub.slug;
                  return (
                    <div
                      key={sub.id}
                      className={`cm-subcat${selectedSubCategory === sub.id ? ' active' : ''}${isLocked ? ' locked' : ''}${!hasAccess ? ' opacity-60' : ''}`}
                      onClick={() => {
                        if (!hasAccess) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        if (lockedSubCategory === (sub.slug || sub.id)) {
                          setSelectedSubCategory(null);
                          setLockedSubCategory(null);
                          setSelectedStageId(null);
                        } else if (!lockedSubCategory) {
                          const subKey = sub.slug || sub.id;
                          setSelectedSubCategory(subKey);
                          setLockedSubCategory(subKey);
                          setSelectedStageId(null);
                        }
                      }}
                      style={{ cursor: !hasAccess || (lockedSubCategory && !isLocked) ? 'not-allowed' : 'pointer' }}
                      title={!hasAccess ? restrictionInfo.reason : (isLocked ? 'Click to unlock and show all options' : 'Click to select and hide other options')}
                    >
                      <span className="cm-subcat-name">
                        {!hasAccess && <Lock className="w-3.5 h-3.5 inline-block mr-1.5 text-red-500" />}
                        {isLocked && hasAccess && <Lock className="w-3.5 h-3.5 inline-block mr-1.5" />}
                        {(() => {
                          const SubIcon = SUBCAT_ICONS[sub.slug] || SUBCAT_ICONS[sub.id];
                          return SubIcon ? <SubIcon className="w-3.5 h-3.5 inline-block mr-1.5" /> : null;
                        })()}
                        {sub.name}
                      </span>
                      <span className="cm-subcat-pct">{subPct}%</span>
                    </div>
                  );
                })}
            </div>
          )}

          {stages.length > 0 ? (
            <>
              {/* ── STATIC REFERENCE MAP ── */}
              <div className="cm-card static-card" data-tour="career-stages">
                <div className="cm-card-header">
                  <div>
                    <div className="cm-card-title">{currentSubCategory?.fullName}</div>
                  </div>
                </div>
                <div className="cm-timeline">
                  {stages.map((stage, i) => {
                    const status = getStageStatus(stage.id);
                    return (
                      <div key={stage.id} className="cm-stage-wrap">
                        <div className="cm-stage">
                          <div className="cm-circle circle-inprog">{stage.id}</div>
                          <div className="cm-stage-info">
                            <div className="cm-stage-name">{stage.name}</div>
                            <div className="cm-stage-desc">{stage.description}</div>
                            <span className={`cm-badge ${badgeClass(status)}`}>{badgeLabel(status)}</span>
                          </div>
                        </div>
                        {i < stages.length - 1 && <div className="cm-connector" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── EDITABLE INTERACTIVE MAP ── */}
              <div className="cm-card">
                <div className="cm-card-header">
                  <div>
                    <div className="cm-card-label">Your Progress</div>
                    <div className="cm-card-label-sub">Click a stage to update your status</div>
                  </div>
                  <div className="cm-card-right">
                    <div className="cm-pct-val">{progressPercentage}%</div>
                    <div className="cm-stages-count">{completedCount}/{stages.length} stages</div>
                    <Button
                      variant="secondary"
                      size="md"
                      className="mt-3"
                      onClick={async () => {
                        try {
                          const token = careerMapService.getAuthToken();
                          if (token) {
                            const subcategoryId = careerMapService.findSubcategoryId(categories, selectedSubCategory);
                            if (subcategoryId) await careerMapService.resetProgress(subcategoryId);
                          }
                          const keys = stages.map(s => getKey(selectedSubCategory, s.id));
                          setStageData(prev => {
                            const next = { ...prev };
                            keys.forEach(k => delete next[k]);
                            return next;
                          });
                          setSelectedStageId(null);
                        } catch (error) {
                          console.error('❌ Error resetting progress:', error);
                        }
                      }}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
                <div className="cm-progress-bar">
                  <div className="cm-progress-fill" style={{ width: `${progressPercentage}%` }} />
                </div>

                {/* Interactive timeline */}
                {!selectedStageId ? (
                  <>
                    <div className="cm-timeline">
                      {stages.map((stage, i) => {
                        const status = getStageStatus(stage.id);
                        const prevStage = i > 0 ? stages[i - 1] : null;
                        const prevStatus = prevStage ? getStageStatus(prevStage.id) : null;
                        const isUnlocked = i === 0 || prevStatus === 'completed';
                        
                        return (
                          <div key={stage.id} className="cm-stage-wrap">
                            <div
                              className={`cm-stage ${isUnlocked ? 'clickable' : 'locked'}`}
                              onClick={() => isUnlocked && setSelectedStageId(stage.id)}
                              style={{ cursor: isUnlocked ? 'pointer' : 'not-allowed', opacity: isUnlocked ? 1 : 0.5 }}
                            >
                              <div className={`cm-circle ${circleClass(status)}`}>{stage.id}</div>
                              <div className="cm-stage-info">
                                <div className="cm-stage-name">{stage.name}</div>
                                <div className="cm-stage-desc">{stage.description}</div>
                                <span className="cm-click-hint">{isUnlocked ? 'Click to update' : '🔒 Complete previous stage'}</span>
                              </div>
                            </div>
                            {i < stages.length - 1 && <div className="cm-connector" />}
                          </div>
                        );
                      })}
                    </div>

                    {/* Saved Inputs Section - Simplified Single Strip */}
                    {stages.some(stage => {
                      const stageFormData = getStageFormData(stage.id);
                      return stage.fields && stage.fields.some(field => stageFormData[field]);
                    }) && (
                      <div className="cm-saved-inputs-section">
                        <h3 className="cm-saved-inputs-title">📝 Stages detailed information</h3>
                        <div className="cm-saved-single-strip">
                          {stages.map(stage => {
                            const stageFormData = getStageFormData(stage.id);
                            const hasInputData = stage.fields && stage.fields.some(field => stageFormData[field]);
                            if (!hasInputData) return null;

                            const status = getStageStatus(stage.id);
                            const statusColor = status === 'completed' ? '#10b981' : status === 'in-progress' ? '#c33c3d' : '#9ca3af';

                            return (
                              <div key={stage.id} className="cm-saved-strip-item">
                                <svg className="cm-saved-strip-bar" viewBox="0 0 20 100" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M 0 0 L 18 0 Q 4 50 18 100 L 0 100 Z" fill="#c33c3d"/>
                                </svg>
                                <div className="cm-saved-strip-header">
                                  <div className="cm-saved-strip-content">
                                    <div className="cm-saved-strip-name">{stage.name}</div>
                                    <div className="cm-saved-strip-fields">
                                      {stage.fields.map(field => {
                                        const value = stageFormData[field];
                                        if (!value) return null;
                                        return (
                                          <span key={field} className="cm-saved-strip-field">
                                            <strong>{stage.fieldLabels?.[field] || field}:</strong> {value}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </div>
                                  <div className="cm-saved-strip-status" style={{ color: statusColor }}>
                                    {status === 'completed' ? '✓ Achieved' : status === 'in-progress' ? '⏳ In Progress' : 'Not Started'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  selectedStage && (
                    <div className="cm-form">
                      <div className="cm-form-header">
                        <div className="cm-form-circle">{selectedStage.id}</div>
                        <div>
                          <div className="cm-form-stage-meta">Stage {selectedStage.id} of {stages.length}</div>
                          <div className="cm-form-title">{selectedStage.name}</div>
                          <div className="cm-form-desc">{selectedStage.description}</div>
                        </div>
                      </div>

                      {selectedStage.fields?.map(field => (
                        <div key={field} className="cm-field">
                          <label className="cm-label">{selectedStage.fieldLabels?.[field] || field}</label>
                          <input
                            className="cm-input"
                            type="text"
                            placeholder={`Enter ${selectedStage.fieldLabels?.[field] || field}`}
                            value={getStageFormData(selectedStage.id)[field] || ''}
                            onChange={e => updateStageData(selectedStage.id, field, e.target.value)}
                          />
                        </div>
                      ))}

                      <div className="cm-field">
                        <label className="cm-label">Status</label>
                        <select
                          className="cm-select"
                          value={getStageStatus(selectedStage.id)}
                          onChange={e => updateStageData(selectedStage.id, 'status', e.target.value)}
                        >
                          <option value="not-started">Not Started</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>

                      <div className="cm-form-actions">
                        <button className="cm-btn-back" onClick={() => setSelectedStageId(null)}>← Back to Map</button>
                        <button className="cm-btn-save" onClick={saveStageData}>Save & Close</button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </>
          ) : (
            <div className="cm-empty">No stages available for this career path yet.</div>
          )}

        </div>
      </div>

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </>
  );
};

export default CareerMap;